import {
  ACESFilmicToneMapping,
  AmbientLight,
  DirectionalLight,
  Fog,
  HemisphereLight,
  Light,
  LOD,
  MathUtils,
  Mesh,
  MeshPhysicalMaterial,
  Object3D,
  PCFShadowMap,
  PMREMGenerator,
  PointLight,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
  WebGLRenderTarget,
} from "three";
import { RoomEnvironment } from "three/addons/environments/RoomEnvironment.js";

import type { WorldTileId } from "../world/schema/tile.ts";
import { ThirdPersonCameraRig } from "./cameraRig.ts";
import { ObstructionFadeController } from "./effects/obstructionFade.ts";
import { createSceneStage } from "./scene.ts";
import type {
  GameRenderer,
  GameRendererOptions,
  GameRenderSnapshot,
  PlayerRenderSnapshot
} from "./types.ts";
import {
  AUTHORED_INTERIORS,
  authoredInteriorById,
} from "../config/interiors.ts";
import {
  INTERIOR_DYNAMIC_POINT_LIGHT_BUDGET,
} from "./interiorLighting.ts";
import { CHARACTER_PRESENTATION_CONFIG } from "../config/character.ts";
import type { RoadVehiclePresentation } from "../sim/vehicles/index.ts";
import { visualRealismEnabled } from "../config/visualRealism.ts";
import {
  copyRoadVehiclePresentations,
  interpolateRoadVehiclePresentations,
} from "./roadVehicleInterpolation.ts";
import type { PublicTransportVehiclePresentation } from
  "../sim/publicTransport/index.ts";
import {
  copyPublicTransportPresentations,
  interpolatePublicTransportPresentations,
} from "./publicTransportInterpolation.ts";

function copyPlayer(player: PlayerRenderSnapshot): PlayerRenderSnapshot {
  return {
    id: player.id,
    position: {
      x: player.position.x,
      y: player.position.y,
      z: player.position.z
    },
    yawRadians: player.yawRadians,
    lookYawRadians: player.lookYawRadians,
    planarSpeedMetersPerSecond: player.planarSpeedMetersPerSecond,
    grounded: player.grounded,
    ...(player.isFlying === undefined
      ? {}
      : { isFlying: player.isFlying }),
    ...(player.usesAerialVisibility === undefined
      ? {}
      : { usesAerialVisibility: player.usesAerialVisibility }),
    ...(player.verticalSpeedMetersPerSecond === undefined
      ? {}
      : {
          verticalSpeedMetersPerSecond:
            player.verticalSpeedMetersPerSecond,
        }),
    ...(player.isSprinting === undefined
      ? {}
      : { isSprinting: player.isSprinting }),
    ...(player.cameraTargetHeightOffsetMeters === undefined
      ? {}
      : {
          cameraTargetHeightOffsetMeters:
            player.cameraTargetHeightOffsetMeters,
        }),
    ...(player.cameraDistanceMeters === undefined
      ? {}
      : { cameraDistanceMeters: player.cameraDistanceMeters }),
    ...(player.vehicleSeatRole === undefined
      ? {}
      : { vehicleSeatRole: player.vehicleSeatRole }),
    ...(player.mountedVehicleId === undefined
      ? {}
      : { mountedVehicleId: player.mountedVehicleId }),
    ...(player.vehicleSpeedMetersPerSecond === undefined
      ? {}
      : { vehicleSpeedMetersPerSecond: player.vehicleSpeedMetersPerSecond }),
    ...(player.vehicleHeadingYawRadians === undefined
      ? {}
      : { vehicleHeadingYawRadians: player.vehicleHeadingYawRadians }),
    ...(player.vehicleSteeringRadians === undefined
      ? {}
      : { vehicleSteeringRadians: player.vehicleSteeringRadians }),
    ...(player.vehicleLookBehind === undefined
      ? {}
      : { vehicleLookBehind: player.vehicleLookBehind }),
  };
}

function shortestAngleDelta(fromRadians: number, toRadians: number): number {
  return Math.atan2(
    Math.sin(toRadians - fromRadians),
    Math.cos(toRadians - fromRadians)
  );
}

const AUTHORED_INTERIOR_LANDMARK_IDS = new Set(
  AUTHORED_INTERIORS.map((interior) => interior.landmarkId),
);

export function createGameRenderer(options: GameRendererOptions): GameRenderer {
  const realismEnabled = visualRealismEnabled(window.location.search);
  const context = options.canvas.getContext("webgl2", {
    antialias: false,
    alpha: false,
    depth: true,
    powerPreference: "high-performance",
    premultipliedAlpha: false
  });

  if (context === null) {
    throw new Error("Vilnius requires a browser with WebGL 2 support.");
  }

  options.canvas.dataset.vilniusCanvas = "";

  const renderer = new WebGLRenderer({
    canvas: options.canvas,
    context,
    antialias: false,
    alpha: false
  });
  // Three's first-use shader diagnostics synchronously query driver logs for
  // every streamed material variant. Valid programs are covered by the visual
  // gates; avoid turning those diagnostic reads into traversal hitches.
  renderer.debug.checkShaderErrors = false;
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.toneMapping = ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.94;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFShadowMap;

  const stage = createSceneStage(
    options.config.quality.shadowMapSize,
    options.config.environment,
    options.config.characterMotion,
    { visualRealismEnabled: realismEnabled },
  );
  const roomEnvironment = new RoomEnvironment();
  const pmremGenerator = new PMREMGenerator(renderer);
  const interiorEnvironmentTarget = pmremGenerator.fromScene(
    roomEnvironment,
    0.04,
  );
  roomEnvironment.dispose();
  pmremGenerator.dispose();
  const interiorShaderWarmupScene = new Scene();
  interiorShaderWarmupScene.fog = new Fog(0xa7afa8, 120, 680);
  interiorShaderWarmupScene.environment = interiorEnvironmentTarget.texture;
  interiorShaderWarmupScene.environmentIntensity = 0.62;
  const interiorShaderWarmupLights = [
    new AmbientLight(0xffffff, 1),
    new HemisphereLight(0xffffff, 0xffffff, 1),
    new DirectionalLight(0xffffff, 1),
    ...Array.from(
      { length: INTERIOR_DYNAMIC_POINT_LIGHT_BUDGET },
      () => new PointLight(0xffffff, 1, 10, 2),
    ),
  ];
  const interiorShaderWarmupDirection = interiorShaderWarmupLights[2];
  if (interiorShaderWarmupDirection instanceof DirectionalLight) {
    interiorShaderWarmupDirection.castShadow = true;
  }
  interiorShaderWarmupScene.add(...interiorShaderWarmupLights);
  const cameraRig = new ThirdPersonCameraRig(
    options.config.camera,
    options.cameraCollisionQuery,
  );
  const obstructionFade =
    options.config.camera.obstructionFadeOpacity === undefined
      ? null
      : new ObstructionFadeController(
          options.config.camera.obstructionFadeOpacity,
        );

  let previousPlayer: PlayerRenderSnapshot | null = null;
  let currentPlayer: PlayerRenderSnapshot | null = null;
  let previousTimeSeconds = 0;
  let currentTimeSeconds = 0;
  let lastSourceTimeSeconds: number | null = null;
  let previousDistanceMeters = 0;
  let currentDistanceMeters = 0;
  let lastRenderedTimeSeconds: number | null = null;
  let lastSnapshotTick: number | null = null;
  let previousRoadVehicles: readonly RoadVehiclePresentation[] | null = null;
  let currentRoadVehicles: readonly RoadVehiclePresentation[] | null = null;
  let renderedPlayerPosition = { x: 0, y: 0, z: 0 };
  let renderedDeltaSeconds = 0;
  let lastRenderFrameStartedAtMilliseconds: number | null = null;
  let localVerticalCorrectionOffsetMeters = 0;
  let localCorrectionResponsePerSecond = 0;
  let previousPublicTransport:
    readonly PublicTransportVehiclePresentation[] | null = null;
  let currentPublicTransport:
    readonly PublicTransportVehiclePresentation[] | null = null;
  let lastFrameCpuMs = 0;
  let lastSceneUpdateCpuMs = 0;
  let lastDrawCpuMs = 0;
  let shadowMapUpdateCount = 0;
  let shadowMapUpdatedLastFrame = false;
  const tileObstructionUnregister = new Map<
    WorldTileId,
    readonly (() => void)[]
  >();
  let landmarkObstructionUnregister: readonly (() => void)[] = [];
  let disposed = false;
  const authoredAssetWarmupTarget = new WebGLRenderTarget(1, 1);
  let authoredAssetWarmupTail = Promise.resolve();
  let characterInspectionView: "front" | "back" | "face" | null = null;
  let characterInspectionBlend = 0;

  function hasTransmissiveMaterial(object: Object3D): boolean {
    let hasTransmission = false;
    object.traverse((child) => {
      if (hasTransmission || !(child instanceof Mesh)) return;
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material];
      hasTransmission = materials.some(
        (material) =>
          material instanceof MeshPhysicalMaterial &&
          material.transmission > 0,
      );
    });
    return hasTransmission;
  }

  function belongsToAuthoredInterior(object: Object3D): boolean {
    const landmarkId = object.userData.landmarkId;
    return typeof landmarkId === "string" &&
      AUTHORED_INTERIOR_LANDMARK_IDS.has(landmarkId);
  }

  async function compileInteriorShaderVariant(object: Object3D): Promise<void> {
    const visibleObjectLights: Light[] = [];
    object.traverse((child) => {
      if (child instanceof Light && child.visible) {
        visibleObjectLights.push(child);
        child.visible = false;
      }
    });
    const previousTarget = renderer.getRenderTarget();
    let compilation: Promise<unknown>;
    try {
      // compileAsync gathers lights synchronously before returning its promise.
      // Hide lights already owned by the object for that short setup step so
      // the dedicated warm-up scene supplies the exact runtime shader budget
      // instead of adding a duplicate sun, hemisphere and fill light.
      // Interior presentation is drawn to the canvas. Compiling while the
      // 1 px warm-up target is bound would cache a linear/no-tone-mapping
      // variant and still leave the real sRGB canvas variant cold.
      renderer.setRenderTarget(null);
      compilation = renderer.compileAsync(
        object,
        cameraRig.camera,
        interiorShaderWarmupScene,
      );
    } finally {
      renderer.setRenderTarget(previousTarget);
      for (const light of visibleObjectLights) {
        light.visible = true;
      }
    }
    await compilation;
  }

  async function warmAuthoredAssetShaders(object: Object3D): Promise<void> {
    const warmExteriorTransmission = hasTransmissiveMaterial(object);
    const warmInteriorVariant = belongsToAuthoredInterior(object);
    if ((!warmExteriorTransmission && !warmInteriorVariant) || disposed) return;
    const task = authoredAssetWarmupTail.then(async () => {
      if (disposed) return;
      const objectStates: Array<Readonly<{
        object: Object3D;
        visible: boolean;
        frustumCulled: boolean;
        lodAutoUpdate: boolean | null;
      }>> = [];
      object.traverse((child) => {
        const lod = child instanceof LOD ? child : null;
        objectStates.push({
          object: child,
          visible: child.visible,
          frustumCulled: child.frustumCulled,
          lodAutoUpdate: lod?.autoUpdate ?? null,
        });
        if (!(child instanceof Light)) {
          child.visible = true;
        }
        child.frustumCulled = false;
        if (lod !== null) lod.autoUpdate = false;
      });
      const previousTarget = renderer.getRenderTarget();
      try {
        if (warmExteriorTransmission) {
          stage.scene.add(object);
          renderer.setRenderTarget(authoredAssetWarmupTarget);
          await renderer.compileAsync(object, cameraRig.camera, stage.scene);
          renderer.render(stage.scene, cameraRig.camera);
        }
        if (warmInteriorVariant) {
          await compileInteriorShaderVariant(object);
        }
      } finally {
        object.removeFromParent();
        renderer.setRenderTarget(previousTarget);
        for (const state of objectStates) {
          state.object.visible = state.visible;
          state.object.frustumCulled = state.frustumCulled;
          if (state.lodAutoUpdate !== null) {
            (state.object as LOD).autoUpdate = state.lodAutoUpdate;
          }
        }
      }
    });
    authoredAssetWarmupTail = task.catch(() => undefined);
    await task;
  }

  function withAuthoredAssetWarmup(
    loadOptions: Parameters<typeof stage.loadVilniusLandmarks>[1],
  ): NonNullable<Parameters<typeof stage.loadVilniusLandmarks>[1]> {
    return {
      ...loadOptions,
      prepareObjectForDisplay: warmAuthoredAssetShaders,
    };
  }

  function unregisterTileObstructions(tileId: WorldTileId): void {
    const unregister = tileObstructionUnregister.get(tileId);
    if (unregister === undefined) {
      return;
    }
    for (const remove of unregister) {
      remove();
    }
    tileObstructionUnregister.delete(tileId);
  }

  function registerTileObstructions(tileId: WorldTileId): void {
    if (obstructionFade === null) {
      return;
    }
    const unregister = stage
      .getWorldTileCameraObstructions(tileId)
      .map((target) =>
        obstructionFade.register(target.id, target.object)
      );
    if (unregister.length > 0) {
      tileObstructionUnregister.set(tileId, unregister);
    }
  }

  function unregisterAllTileObstructions(): void {
    for (const tileId of [...tileObstructionUnregister.keys()]) {
      unregisterTileObstructions(tileId);
    }
  }

  function clearLandmarkObstructions(): void {
    for (const unregister of landmarkObstructionUnregister) {
      unregister();
    }
    landmarkObstructionUnregister = [];
  }

  function refreshLandmarkObstructions(): void {
    clearLandmarkObstructions();
    if (obstructionFade !== null) {
      landmarkObstructionUnregister = stage
        .getLandmarkCameraObstructions()
        .map((target) => obstructionFade.register(target.id, target.object));
    }
  }

  function resize(): void {
    if (disposed) {
      return;
    }

    const width = Math.max(1, Math.round(options.canvas.clientWidth));
    const height = Math.max(1, Math.round(options.canvas.clientHeight));
    const pixelRatio = Math.min(
      options.config.quality.maxPixelRatio,
      window.devicePixelRatio
    );
    const desiredWidth = Math.round(width * pixelRatio);
    const desiredHeight = Math.round(height * pixelRatio);

    if (
      options.canvas.width !== desiredWidth ||
      options.canvas.height !== desiredHeight
    ) {
      renderer.setPixelRatio(pixelRatio);
      renderer.setSize(width, height, false);
      cameraRig.setAspect(width / height);
    }
  }

  return {
    setSnapshot(snapshot, presentationOptions = {}): void {
      const nextPlayer = copyPlayer(snapshot.player);
      const sourceTimeSeconds = snapshot.timeSeconds ??
        lastSourceTimeSeconds ?? currentTimeSeconds;
      const sourceTimeAdvanceSeconds = lastSourceTimeSeconds === null
        ? 0
        : MathUtils.clamp(
            sourceTimeSeconds - lastSourceTimeSeconds,
            0,
            0.1,
          );
      const nextTimeSeconds = currentPlayer === null ||
          presentationOptions.resetInterpolation === true
        ? sourceTimeSeconds
        : currentTimeSeconds + sourceTimeAdvanceSeconds;
      const sameTickRelocation =
        lastSnapshotTick === snapshot.tick &&
        currentPlayer !== null &&
        (nextPlayer.position.x !== currentPlayer.position.x ||
          nextPlayer.position.y !== currentPlayer.position.y ||
          nextPlayer.position.z !== currentPlayer.position.z);
      const timeReset = presentationOptions.resetInterpolation === true ||
        (lastSnapshotTick !== null && snapshot.tick < lastSnapshotTick) ||
        sameTickRelocation;
      const correctionSmoothing =
        presentationOptions.verticalCorrectionSmoothing;
      if (correctionSmoothing !== undefined && currentPlayer !== null) {
        const correctionY =
          renderedPlayerPosition.y - nextPlayer.position.y;
        if (
          Math.abs(correctionY) <= correctionSmoothing.maximumDistanceMeters
        ) {
          localVerticalCorrectionOffsetMeters = correctionY;
          localCorrectionResponsePerSecond =
            correctionSmoothing.responsePerSecond;
        } else {
          localVerticalCorrectionOffsetMeters = 0;
          localCorrectionResponsePerSecond = 0;
        }
        previousPlayer = nextPlayer;
        currentPlayer = nextPlayer;
        previousTimeSeconds = nextTimeSeconds;
        currentTimeSeconds = nextTimeSeconds;
        previousDistanceMeters = currentDistanceMeters;
        lastSourceTimeSeconds = sourceTimeSeconds;
        lastSnapshotTick = snapshot.tick;
        return;
      }
      if (timeReset) {
        previousPlayer = nextPlayer;
        currentPlayer = nextPlayer;
        previousTimeSeconds = nextTimeSeconds;
        currentTimeSeconds = nextTimeSeconds;
        previousDistanceMeters = 0;
        currentDistanceMeters = 0;
        lastRenderedTimeSeconds = null;
        lastSourceTimeSeconds = sourceTimeSeconds;
        lastRenderFrameStartedAtMilliseconds = null;
        localVerticalCorrectionOffsetMeters = 0;
        localCorrectionResponsePerSecond = 0;
        lastSnapshotTick = snapshot.tick;
        return;
      }
      const nextDistanceMeters = currentPlayer === null
        ? currentDistanceMeters
        : currentDistanceMeters +
          Math.hypot(
            nextPlayer.position.x - currentPlayer.position.x,
            nextPlayer.position.z - currentPlayer.position.z,
          );
      previousPlayer = currentPlayer === null ? nextPlayer : currentPlayer;
      currentPlayer = nextPlayer;
      previousTimeSeconds = currentPlayer === previousPlayer
        ? nextTimeSeconds
        : currentTimeSeconds;
      currentTimeSeconds = nextTimeSeconds;
      previousDistanceMeters = currentPlayer === previousPlayer
        ? nextDistanceMeters
        : currentDistanceMeters;
      currentDistanceMeters = nextDistanceMeters;
      lastSourceTimeSeconds = sourceTimeSeconds;
      lastSnapshotTick = snapshot.tick;
    },

    setLocalLookYaw(yawRadians): void {
      if (!Number.isFinite(yawRadians)) return;
      if (previousPlayer !== null) {
        previousPlayer = { ...previousPlayer, lookYawRadians: yawRadians };
      }
      if (currentPlayer !== null) {
        currentPlayer = { ...currentPlayer, lookYawRadians: yawRadians };
      }
    },

    getRenderedPlayerPosition(): Readonly<{
      x: number;
      y: number;
      z: number;
    }> {
      return { ...renderedPlayerPosition };
    },

    getRenderedMotionDiagnostics() {
      const cameraState = cameraRig.getState();
      return {
        playerPosition: { ...renderedPlayerPosition },
        cameraTarget: { ...cameraState.target },
        cameraDesiredPosition: { ...cameraState.desiredPosition },
        cameraResolvedPosition: { ...cameraState.resolvedPosition },
        cameraDesiredDistanceMeters: cameraState.desiredDistanceMeters,
        cameraResolvedDistanceMeters: cameraState.resolvedDistanceMeters,
        cameraObstructingObjectIds: [...cameraState.obstructingObjectIds],
        deltaSeconds: renderedDeltaSeconds,
      };
    },

    setPlayerAppearance(appearance): void {
      stage.mannequin.setAppearance(appearance);
    },

    setRemotePlayerPresentations(presentations): void {
      stage.setRemotePlayerPresentations(presentations);
    },

    setCharacterInspectionView(view): void {
      characterInspectionView = view;
    },

    setDevelopmentTileSet(tileSet, worldOptions = {}) {
      unregisterAllTileObstructions();
      return stage.setDevelopmentTileSet(tileSet, worldOptions);
    },

    clearDevelopmentTileSet(): void {
      stage.clearDevelopmentTileSet();
    },

    attachWorldTile(tile, worldOptions = {}) {
      unregisterTileObstructions(tile.id);
      const metrics = stage.attachWorldTile(tile, worldOptions);
      registerTileObstructions(tile.id);
      return metrics;
    },

    async attachWorldTileInPhases(tile, worldOptions, shouldContinue) {
      const metrics = await stage.attachWorldTileInPhases(
        tile,
        worldOptions,
        () => new Promise<void>((resolve) => {
          setTimeout(resolve, 0);
        }),
        shouldContinue,
      );
      if (metrics === null) return null;
      unregisterTileObstructions(tile.id);
      registerTileObstructions(tile.id);
      return metrics;
    },

    updateWorldTile(tileId, worldOptions) {
      unregisterTileObstructions(tileId);
      const metrics = stage.updateWorldTile(tileId, worldOptions);
      registerTileObstructions(tileId);
      return metrics;
    },

    detachWorldTile(tileId): boolean {
      unregisterTileObstructions(tileId);
      return stage.detachWorldTile(tileId);
    },

    setWorldTileLod(tileId, lod) {
      unregisterTileObstructions(tileId);
      const metrics = stage.setWorldTileLod(tileId, lod);
      registerTileObstructions(tileId);
      return metrics;
    },

    getWorldRenderDiagnostics() {
      return stage.getWorldRenderDiagnostics();
    },

    getPerformanceDiagnostics() {
      return {
        drawCalls: renderer.info.render.calls,
        triangles: renderer.info.render.triangles,
        points: renderer.info.render.points,
        lines: renderer.info.render.lines,
        geometries: renderer.info.memory.geometries,
        textures: renderer.info.memory.textures,
        shaderPrograms: renderer.info.programs?.length ?? 0,
        frameCpuMs: lastFrameCpuMs,
        sceneUpdateCpuMs: lastSceneUpdateCpuMs,
        drawCpuMs: lastDrawCpuMs,
        shadowMapUpdateCount,
        shadowMapUpdatedLastFrame,
      };
    },

    async prepareInitialShaderVariants(): Promise<void> {
      const objectStates: Array<Readonly<{
        object: Object3D;
        visible: boolean;
        frustumCulled: boolean;
        lodAutoUpdate: boolean | null;
      }>> = [];
      stage.scene.traverse((object) => {
        const lod = object instanceof LOD ? object : null;
        objectStates.push({
          object,
          visible: object.visible,
          frustumCulled: object.frustumCulled,
          lodAutoUpdate: lod?.autoUpdate ?? null,
        });
        if (!(object instanceof Light)) {
          object.visible = true;
        }
        object.frustumCulled = false;
        if (lod !== null) lod.autoUpdate = false;
      });
      const previousTarget = renderer.getRenderTarget();
      try {
        renderer.setRenderTarget(authoredAssetWarmupTarget);
        await renderer.compileAsync(stage.scene, cameraRig.camera);
        renderer.render(stage.scene, cameraRig.camera);
        await compileInteriorShaderVariant(stage.scene);
      } finally {
        renderer.setRenderTarget(previousTarget);
        for (const state of objectStates) {
          state.object.visible = state.visible;
          state.object.frustumCulled = state.frustumCulled;
          if (state.lodAutoUpdate !== null) {
            (state.object as LOD).autoUpdate = state.lodAutoUpdate;
          }
        }
      }
    },

    clearWorldTiles(): void {
      unregisterAllTileObstructions();
      stage.clearWorldTiles();
    },

    async loadVilniusLandmarks(manifestUrl, loadOptions) {
      clearLandmarkObstructions();
      const diagnostics = await stage.loadVilniusLandmarks(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
      refreshLandmarkObstructions();
      return diagnostics;
    },

    async loadVilniusBridges(manifestUrl, loadOptions) {
      return stage.loadVilniusBridges(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
    },

    async loadParkedVehicles(manifestUrl, loadOptions) {
      return stage.loadParkedVehicles(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
    },

    async loadCivicSquares(manifestUrl, loadOptions) {
      return stage.loadCivicSquares(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
    },

    async loadVilniusAirport(manifestUrl, loadOptions) {
      return stage.loadVilniusAirport(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
    },

    async loadTraversalNetwork(manifestUrl, loadOptions) {
      return stage.loadTraversalNetwork(
        manifestUrl,
        withAuthoredAssetWarmup(loadOptions),
      );
    },

    async loadModularCityAssets(manifestUrl) {
      return stage.loadModularCityAssets(manifestUrl, {
        prepareObjectForDisplay: warmAuthoredAssetShaders,
      });
    },

    async loadDeferredAuthoredAssets(): Promise<void> {
      await stage.loadDeferredAuthoredAssets();
      refreshLandmarkObstructions();
    },

    startDeferredAuthoredAssetStreaming(): void {
      stage.startDeferredAuthoredAssetStreaming();
    },

    setTraversalShortcutState(access): void {
      stage.setTraversalShortcutState(access);
    },

    async loadBoatFleet(manifestUrl) {
      return stage.loadBoatFleet(manifestUrl);
    },

    setBoatPresentations(presentations): void {
      stage.setBoatPresentations(presentations);
    },

    setRoadVehiclePresentations(presentations): void {
      const next = copyRoadVehiclePresentations(presentations);
      previousRoadVehicles = currentRoadVehicles ?? next;
      currentRoadVehicles = next;
    },

    async loadPublicTransportFleet() {
      return stage.loadPublicTransportFleet();
    },

    setPublicTransportPresentations(presentations): void {
      const next = copyPublicTransportPresentations(presentations);
      previousPublicTransport = currentPublicTransport ?? next;
      currentPublicTransport = next;
    },

    setDistrictVisualSample(sample, viewpointWorldPosition) {
      unregisterAllTileObstructions();
      return stage.setDistrictVisualSample(sample, viewpointWorldPosition);
    },

    setThreeCrossesLandmark(position): void {
      stage.setThreeCrossesLandmark(position);
    },

    setResidentPresentations(presentations, animationTimeSeconds): void {
      stage.setResidentPresentations(presentations, animationTimeSeconds);
    },

    setEnvironment(presentation): void {
      stage.setEnvironment(presentation);
    },

    setInteriorPresentation(presentation): void {
      stage.setInteriorPresentation(presentation);
      obstructionFade?.setIgnoredIds(
        authoredInteriorById(presentation.activeInteriorId)
          ?.cameraObstructionIds ?? [],
      );
    },

    getInteriorLightingState() {
      return stage.getInteriorLightingState();
    },

    scrubEnvironment(deltaHours): void {
      stage.scrubEnvironment(deltaHours);
    },

    toggleEnvironmentFrozen(): boolean {
      return stage.toggleEnvironmentFrozen();
    },

    getEnvironmentState() {
      return stage.getEnvironmentState();
    },

    setVisibilitySettings(settings): void {
      cameraRig.setVisibilityFarMeters(
        settings.grounded.cameraFarMeters,
        settings.flight.cameraFarMeters,
      );
      stage.setFogFarDayMeters(
        settings.grounded.fogFarDayMeters,
        settings.flight.fogFarDayMeters,
      );
    },

    getCameraFarMeters(): number {
      return cameraRig.camera.far;
    },

    pitchBy(deltaPixelsY: number): void {
      cameraRig.pitchBy(deltaPixelsY);
    },

    resetPitch(): void {
      cameraRig.resetPitch();
    },

    zoomBy(deltaPixelsY: number): void {
      cameraRig.zoomBy(deltaPixelsY);
    },

    setCameraCollisionQuery(query): void {
      cameraRig.setCollisionQuery(query);
    },

    registerCameraObstruction(id, object): () => void {
      return obstructionFade?.register(id, object) ?? (() => {});
    },

    unregisterCameraObstruction(id): void {
      obstructionFade?.unregister(id);
    },

    resize,

    render(interpolationAlpha: number): void {
      if (disposed || currentPlayer === null || previousPlayer === null) {
        return;
      }

      const frameStartedAt = performance.now();
      const renderDeltaSeconds = lastRenderFrameStartedAtMilliseconds === null
        ? 0
        : MathUtils.clamp(
            (frameStartedAt - lastRenderFrameStartedAtMilliseconds) / 1_000,
            0,
            0.1,
          );
      lastRenderFrameStartedAtMilliseconds = frameStartedAt;
      resize();
      const alpha = MathUtils.clamp(
        interpolationAlpha,
        0,
        1,
      );
      const interpolatedPosition = {
        x: MathUtils.lerp(previousPlayer.position.x, currentPlayer.position.x, alpha),
        y: MathUtils.lerp(previousPlayer.position.y, currentPlayer.position.y, alpha),
        z: MathUtils.lerp(previousPlayer.position.z, currentPlayer.position.z, alpha)
      };
      if (localCorrectionResponsePerSecond > 0 && renderDeltaSeconds > 0) {
        const correctionRetention = Math.exp(
          -localCorrectionResponsePerSecond * renderDeltaSeconds,
        );
        localVerticalCorrectionOffsetMeters *= correctionRetention;
        if (Math.abs(localVerticalCorrectionOffsetMeters) < 0.0001) {
          localVerticalCorrectionOffsetMeters = 0;
          localCorrectionResponsePerSecond = 0;
        }
      }
      interpolatedPosition.y += localVerticalCorrectionOffsetMeters;
      renderedPlayerPosition = interpolatedPosition;
      const interpolatedYaw = previousPlayer.yawRadians
        + shortestAngleDelta(previousPlayer.yawRadians, currentPlayer.yawRadians) * alpha;
      const interpolatedLookYaw = previousPlayer.lookYawRadians
        + shortestAngleDelta(
            previousPlayer.lookYawRadians,
            currentPlayer.lookYawRadians,
          ) * alpha;
      const interpolatedSpeed = MathUtils.lerp(
        previousPlayer.planarSpeedMetersPerSecond,
        currentPlayer.planarSpeedMetersPerSecond,
        alpha
      );
      const interpolatedVerticalSpeed = MathUtils.lerp(
        previousPlayer.verticalSpeedMetersPerSecond ?? 0,
        currentPlayer.verticalSpeedMetersPerSecond ?? 0,
        alpha,
      );
      const interpolatedVehicleSpeed = MathUtils.lerp(
        previousPlayer.vehicleSpeedMetersPerSecond ?? 0,
        currentPlayer.vehicleSpeedMetersPerSecond ?? 0,
        alpha,
      );
      const interpolatedVehicleHeading = previousPlayer.vehicleHeadingYawRadians === undefined
        ? currentPlayer.vehicleHeadingYawRadians
        : currentPlayer.vehicleHeadingYawRadians === undefined
        ? previousPlayer.vehicleHeadingYawRadians
        : previousPlayer.vehicleHeadingYawRadians + shortestAngleDelta(
            previousPlayer.vehicleHeadingYawRadians,
            currentPlayer.vehicleHeadingYawRadians,
          ) * alpha;
      const interpolatedVehicleSteering = MathUtils.lerp(
        previousPlayer.vehicleSteeringRadians ?? 0,
        currentPlayer.vehicleSteeringRadians ?? 0,
        alpha,
      );
      const interpolatedTimeSeconds = MathUtils.lerp(
        previousTimeSeconds,
        currentTimeSeconds,
        alpha,
      );
      const interpolatedDistanceMeters = MathUtils.lerp(
        previousDistanceMeters,
        currentDistanceMeters,
        alpha,
      );
      const deltaSeconds = lastRenderedTimeSeconds === null
        ? 0
        : Math.max(0, interpolatedTimeSeconds - lastRenderedTimeSeconds);
      lastRenderedTimeSeconds = interpolatedTimeSeconds;
      renderedDeltaSeconds = renderDeltaSeconds;
      characterInspectionBlend = MathUtils.damp(
        characterInspectionBlend,
        characterInspectionView === null ? 0 : 1,
        12,
        Math.min(deltaSeconds, 0.1),
      );

      if (currentRoadVehicles !== null) {
        stage.setRoadVehiclePresentations(
          interpolateRoadVehiclePresentations(
            previousRoadVehicles ?? currentRoadVehicles,
            currentRoadVehicles,
            alpha,
          ),
        );
      }
      if (currentPublicTransport !== null) {
        stage.setPublicTransportPresentations(
          interpolatePublicTransportPresentations(
            previousPublicTransport ?? currentPublicTransport,
            currentPublicTransport,
            alpha,
          ),
        );
      }

      stage.mannequin.setTransform(
        interpolatedPosition,
        interpolatedYaw,
        {
          planarSpeedMetersPerSecond: interpolatedSpeed,
          verticalSpeedMetersPerSecond: interpolatedVerticalSpeed,
          grounded: currentPlayer.grounded,
          isSprinting: currentPlayer.isSprinting ?? false,
          animationTimeSeconds: interpolatedTimeSeconds,
          distanceTravelledMeters: interpolatedDistanceMeters,
        },
        currentPlayer.vehicleSeatRole ?? null,
        interpolatedVehicleSteering,
      );
      stage.updateRemotePlayers(alpha, interpolatedTimeSeconds);
      const aerialVisibilityEnabled =
        currentPlayer.isFlying === true ||
        currentPlayer.usesAerialVisibility === true;
      // Flight exposes roofs and long street walls at once, where the city
      // needs contact and directional shadows most. Keeping them enabled also
      // avoids the flat, ungrounded material read caused by switching them off
      // at the exact moment the camera leaves the street.
      stage.setDirectionalShadowsEnabled(true);
      stage.setAerialVisibilityEnabled(aerialVisibilityEnabled);
      cameraRig.setAerialVisibilityEnabled(aerialVisibilityEnabled);
      if (
        obstructionFade !== null &&
        stage.getLandmarkCameraObstructions().length !==
          landmarkObstructionUnregister.length
      ) {
        refreshLandmarkObstructions();
      }
      const inspection = CHARACTER_PRESENTATION_CONFIG.inspection;
      const inspectionDistance = characterInspectionView === "face"
        ? inspection.faceDistanceMeters
        : inspection.bodyDistanceMeters;
      const inspectionTargetOffset = characterInspectionView === "face"
        ? inspection.faceTargetHeightOffsetMeters
        : inspection.bodyTargetHeightOffsetMeters;
      const regularDistance = currentPlayer.cameraDistanceMeters ??
        options.config.camera.distanceMeters;
      const cameraState = cameraRig.update({
        playerPosition: interpolatedPosition,
        viewYawRadians: interpolatedLookYaw,
        deltaSeconds: renderDeltaSeconds,
        isSprinting: currentPlayer.isSprinting ?? false,
        targetHeightOffsetMeters:
          (currentPlayer.cameraTargetHeightOffsetMeters ?? 0) +
          MathUtils.lerp(0, inspectionTargetOffset, characterInspectionBlend),
        distanceMeters: MathUtils.lerp(
          regularDistance,
          inspectionDistance,
          characterInspectionBlend,
        ),
        shoulderOffsetMeters: MathUtils.lerp(
          options.config.camera.shoulderOffsetMeters,
          0,
          characterInspectionBlend,
        ),
        ...(currentPlayer.mountedVehicleId === null ||
            currentPlayer.mountedVehicleId === undefined
          ? {}
          : {
              vehicleSpeedMetersPerSecond: interpolatedVehicleSpeed,
              vehicleHeadingYawRadians:
                interpolatedVehicleHeading ?? interpolatedYaw,
              vehicleLookBehind: currentPlayer.vehicleLookBehind === true,
            }),
      });
      obstructionFade?.setObstructedIds(cameraState.obstructingObjectIds);
      const sceneUpdateStartedAt = performance.now();
      const environmentState = stage.updateEnvironment(
        deltaSeconds,
        interpolatedPosition,
      );
      const interiorLightingState = stage.getInteriorLightingState();
      if (interiorLightingState.mix01 > 0.001) {
        stage.scene.environment = interiorEnvironmentTarget.texture;
        stage.scene.environmentIntensity =
          0.62 * interiorLightingState.mix01;
      } else {
        stage.scene.environment = null;
        stage.scene.environmentIntensity = 1;
      }
      renderer.toneMappingExposure = environmentState.exposure;
      shadowMapUpdatedLastFrame = true;
      if (shadowMapUpdatedLastFrame) {
        shadowMapUpdateCount += 1;
      }
      lastSceneUpdateCpuMs = performance.now() - sceneUpdateStartedAt;
      const drawStartedAt = performance.now();
      renderer.render(stage.scene, cameraRig.camera);
      lastDrawCpuMs = performance.now() - drawStartedAt;
      lastFrameCpuMs = performance.now() - frameStartedAt;
    },

    dispose(): void {
      if (disposed) {
        return;
      }

      disposed = true;
      unregisterAllTileObstructions();
      for (const unregister of landmarkObstructionUnregister) {
        unregister();
      }
      landmarkObstructionUnregister = [];
      obstructionFade?.dispose();
      stage.dispose();
      for (const light of interiorShaderWarmupLights) {
        light.dispose();
      }
      interiorShaderWarmupScene.clear();
      authoredAssetWarmupTarget.dispose();
      interiorEnvironmentTarget.dispose();
      renderer.dispose();
      delete options.canvas.dataset.vilniusCanvas;
    }
  };
}
